import React, { useEffect, useMemo, useRef, useState } from 'react'
import useAudioManager from '../../../hooks/useAudioManager'
import useWakeLock from '../../../hooks/useWakeLock'
import { hapticImpact } from '../../../utils/haptic'
import {
  WHEEL500_SPIN_DEFAULT_MS,
  WHEEL500_ZONE_CONFIG,
  isWheel500BettingStatus,
  isWheel500ResultStatus,
  isWheel500SpinningStatus
} from '../config/wheel500Config'

/**
 * Круговое колесо секторов для режима X50.
 * Построено по аналогии с DoubleWheel, адаптировано под 54 сектора и 4 зоны (x2/x3/x5/x50).
 */

const DEFAULT_SPIN_DURATION_MS = WHEEL500_SPIN_DEFAULT_MS
const ALIGN_DURATION_MS = 800
const FULL_TURNS = 4
const WHEEL_START_ANGLE = -90
const MAGNIFIER_SCALE = 2.08
const MAGNIFIER_SIZE_PERCENT = 32
const MAGNIFIER_RADIUS_PERCENT = MAGNIFIER_SIZE_PERCENT / 2
const MAGNIFIER_CENTER_Y_PERCENT = 10
const MAGNIFIER_ZOOM_ORIGIN_Y_PERCENT = 4
const MAGNIFIER_TOP_PERCENT = MAGNIFIER_CENTER_Y_PERCENT - MAGNIFIER_RADIUS_PERCENT

const getZoneHex = (zone) => {
  return WHEEL500_ZONE_CONFIG[zone]?.hex || '#64748B'
}

const getSectorDeg = (total) => 360 / total

const getSectorRotation = (sector, total) => {
  const sectorDeg = getSectorDeg(total)
  const normalized = Number.isFinite(Number(sector)) ? Number(sector) : 0
  const centerAngle = WHEEL_START_ANGLE + normalized * sectorDeg + sectorDeg / 2
  // В SVG 0° = право (3 часа), -90° = верх (12 часов), где находится указатель.
  // В DoubleWheel используется CSS conic-gradient (0° = верх), поэтому там -centerAngle.
  // Здесь SVG, поэтому target = -90° (верх), а не 0° (право).
  return -90 - centerAngle
}

const getRandomOffsetInSector = (total) => {
  const sectorDeg = getSectorDeg(total)
  return (Math.random() - 0.5) * 0.8 * sectorDeg
}

const getSectorRotationWithOffset = (sector, total) => {
  return getSectorRotation(sector, total) + getRandomOffsetInSector(total)
}

const getSpinDurationByRaffleSeconds = (raffleSeconds) => {
  const parsed = Number(raffleSeconds)
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_SPIN_DURATION_MS
  return Math.max(0, Math.round(parsed * 1000) - 400)
}

const getSectorArcPath = (innerRadius, outerRadius, startDeg, endDeg) => {
  const toRad = (deg) => (deg * Math.PI) / 180

  const x1 = 50 + outerRadius * Math.cos(toRad(startDeg))
  const y1 = 50 + outerRadius * Math.sin(toRad(startDeg))
  const x2 = 50 + outerRadius * Math.cos(toRad(endDeg))
  const y2 = 50 + outerRadius * Math.sin(toRad(endDeg))

  const x3 = 50 + innerRadius * Math.cos(toRad(endDeg))
  const y3 = 50 + innerRadius * Math.sin(toRad(endDeg))
  const x4 = 50 + innerRadius * Math.cos(toRad(startDeg))
  const y4 = 50 + innerRadius * Math.sin(toRad(startDeg))

  const largeArcFlag = endDeg - startDeg > 180 ? 1 : 0

  return [
    `M ${x1} ${y1}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
    'Z'
  ].join(' ')
}

const formatTimer = (millisecondsValue) => {
  const safeMs = Math.max(0, Number(millisecondsValue || 0))
  const totalTenths = Math.floor(safeMs / 100)
  const seconds = Math.floor(totalTenths / 10)
  const tenths = totalTenths % 10
  return `${seconds}.${tenths}`
}

const Wheel500Wheel = ({
  sectors,
  status,
  winSectorIndex,
  roundId,
  raffleSeconds,
  raffleDurationSeconds,
  raffleEndTimeMs,
  prevSectorIndex,
  countdownMs,
  winZone
}) => {
  const total = sectors.length
  const sectorDeg = getSectorDeg(total)

  const { playSound, stopSound } = useAudioManager({
    spinReel: 'wheel500SpinReel',
    timerStartEnd: 'wheel500TimerStartEnd'
  })

  const [rotation, setRotation] = useState(() => getSectorRotation(prevSectorIndex, total))
  const [spinDurationMs, setSpinDurationMs] = useState(DEFAULT_SPIN_DURATION_MS)
  const [spinFromRotation, setSpinFromRotation] = useState(0)
  const [spinElapsedMs, setSpinElapsedMs] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  // Удержание экрана включённым во время вращения колеса
  useWakeLock(isAnimating)
  const [isAligning, setIsAligning] = useState(false)

  const animatedRoundRef = useRef(null)
  const rotationRef = useRef(getSectorRotation(prevSectorIndex, total))
  const statusRef = useRef(status)
  const winSectorRef = useRef(winSectorIndex)
  const hasPlayedTimerForRoundRef = useRef({ roundId: null, started: false })
  const spinPlayingRef = useRef(false)
  const spinStartedRef = useRef(false)
  const lastCountdownSecondRef = useRef(null)

  // Звук таймера во время фазы ставок
  useEffect(() => {
    const isBettingStatus = isWheel500BettingStatus(status)

    if (!roundId) {
      stopSound('timerStartEnd')
      hasPlayedTimerForRoundRef.current = { roundId: null, started: false }
      return
    }

    if (isBettingStatus) {
      const timerState = hasPlayedTimerForRoundRef.current
      const isNewRound = timerState.roundId !== roundId

      if (isNewRound) {
        hasPlayedTimerForRoundRef.current = { roundId, started: true }
        playSound('timerStartEnd', null, { loop: true })
        return
      }

      if (!timerState.started) {
        hasPlayedTimerForRoundRef.current = { roundId, started: true }
        playSound('timerStartEnd', null, { loop: true })
      }

      return
    }

    stopSound('timerStartEnd')
    hasPlayedTimerForRoundRef.current = { roundId, started: false }
  }, [playSound, roundId, status, stopSound])

  useEffect(() => {
    return () => {
      stopSound('timerStartEnd')
      stopSound('spinReel')
      spinPlayingRef.current = false
    }
  }, [stopSound])

  useEffect(() => {
    rotationRef.current = rotation
  }, [rotation])

  useEffect(() => {
    statusRef.current = status
    winSectorRef.current = winSectorIndex
  }, [status, winSectorIndex])

  // Тактильные тики на последних секундах таймера ставок (3 → 2 → 1)
  useEffect(() => {
    if (!isWheel500BettingStatus(status)) {
      lastCountdownSecondRef.current = null
      return
    }

    const remainingMs = Number(countdownMs || 0)
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) return

    const currentSecond = Math.ceil(remainingMs / 1000)
    const previousSecond = lastCountdownSecondRef.current

    if (previousSecond === currentSecond) return
    lastCountdownSecondRef.current = currentSecond

    if (
      previousSecond !== null
      && currentSecond < previousSecond
      && currentSecond >= 1
      && currentSecond <= 3
    ) {
      hapticImpact('light')
    }
  }, [countdownMs, status])

  // Сброс позиции при смене раунда
  useEffect(() => {
    if (!roundId) return

    const currentStatus = statusRef.current
    const currentWinSector = winSectorRef.current

    // Если пришли посреди raffle — начинаем анимацию с позиции предыдущего сектора
    if (isWheel500SpinningStatus(currentStatus) && Number.isFinite(Number(currentWinSector))) {
      animatedRoundRef.current = null
      spinStartedRef.current = false
      setIsAnimating(false)
      setIsAligning(false)
      const startRotation = getSectorRotation(prevSectorIndex, total)
      rotationRef.current = startRotation
      setRotation(startRotation)
      return
    }

    animatedRoundRef.current = null
    spinStartedRef.current = false
    setIsAnimating(false)

    // Плавно докручиваем от текущей позиции до центра prevSector
    const targetCenterRotation = getSectorRotation(prevSectorIndex, total)
    const currentRotation = Number(rotationRef.current || 0)
    const diff = targetCenterRotation - currentRotation
    const normalizedDiff = ((diff % 360) + 540) % 360 - 180
    const alignTarget = currentRotation + normalizedDiff

    if (Math.abs(normalizedDiff) > 0.5) {
      setIsAligning(true)
      setSpinDurationMs(ALIGN_DURATION_MS)
      requestAnimationFrame(() => {
        rotationRef.current = alignTarget
        setRotation(alignTarget)
      })
    } else {
      setIsAligning(false)
      setSpinDurationMs(DEFAULT_SPIN_DURATION_MS)
      rotationRef.current = targetCenterRotation
      setRotation(targetCenterRotation)
    }
  }, [prevSectorIndex, roundId, total])

  // Фиксация позиции при result/finished
  useEffect(() => {
    if (!roundId) return
    if (!isWheel500ResultStatus(status)) return
    if (!Number.isFinite(Number(winSectorIndex))) return

    if (animatedRoundRef.current === roundId && spinStartedRef.current) return

    // При приходе посреди result — ставим в центр сектора (без рандомного смещения)
    const syncedRotation = getSectorRotation(winSectorIndex, total)
    spinStartedRef.current = false
    setIsAnimating(false)
    setIsAligning(false)
    setSpinDurationMs(DEFAULT_SPIN_DURATION_MS)
    rotationRef.current = syncedRotation
    setRotation(syncedRotation)
  }, [roundId, status, winSectorIndex, total])

  // Запуск анимации при raffle
  useEffect(() => {
    if (!roundId || !Number.isFinite(Number(winSectorIndex))) return

    if (isWheel500SpinningStatus(status)) {
      if (animatedRoundRef.current === roundId) return

      animatedRoundRef.current = roundId

      const nextSpinDuration = getSpinDurationByRaffleSeconds(raffleDurationSeconds ?? (raffleEndTimeMs == null ? raffleSeconds : DEFAULT_SPIN_DURATION_MS / 1000))
      const endTime = Number(raffleEndTimeMs)
      const remainingMs = raffleEndTimeMs != null && Number.isFinite(endTime) ? endTime - Date.now() - 400 : nextSpinDuration
      const currentRotation = Number(rotationRef.current || 0)
      const targetBaseRotation = getSectorRotationWithOffset(winSectorIndex, total)
      const minTargetRotation = currentRotation - FULL_TURNS * 360
      const turnsOffset = Math.ceil((targetBaseRotation - minTargetRotation) / 360)
      const targetRotation = targetBaseRotation - 360 * turnsOffset

      if (remainingMs <= 0 || nextSpinDuration <= 0) {
        spinStartedRef.current = false
        rotationRef.current = targetRotation
        setRotation(targetRotation)
        return
      }

      spinStartedRef.current = true
      setIsAligning(false)
      setSpinFromRotation(currentRotation)
      setSpinElapsedMs(Math.max(0, nextSpinDuration - remainingMs))
      setSpinDurationMs(nextSpinDuration)
      setRotation(targetRotation)
      setIsAnimating(true)
      stopSound('timerStartEnd')
      if (nextSpinDuration - remainingMs < 500) hapticImpact('medium')
      if (!spinPlayingRef.current) {
        playSound('spinReel', null, { loop: true })
        spinPlayingRef.current = true
      }
      return
    }

    if (isWheel500ResultStatus(status)) {
      stopSound('spinReel')
      spinPlayingRef.current = false

      // Докрут к центру выигрышного сектора при показе результата
      if (Number.isFinite(Number(winSectorIndex)) && animatedRoundRef.current === roundId && spinStartedRef.current) {
        const centerRotation = getSectorRotation(winSectorIndex, total)
        const currentRotation = Number(rotationRef.current || 0)
        const diff = centerRotation - currentRotation
        const normalizedDiff = ((diff % 360) + 540) % 360 - 180
        const alignTarget = currentRotation + normalizedDiff

        spinStartedRef.current = false
        if (Math.abs(normalizedDiff) > 0.5) {
          setIsAnimating(false)
          setIsAligning(true)
          setSpinDurationMs(ALIGN_DURATION_MS)
          requestAnimationFrame(() => {
            rotationRef.current = alignTarget
            setRotation(alignTarget)
          })
        } else {
          setIsAnimating(false)
          setIsAligning(false)
          rotationRef.current = centerRotation
          setRotation(centerRotation)
        }
      } else {
        setIsAnimating(false)
        setIsAligning(false)
      }
    }
  }, [playSound, raffleDurationSeconds, raffleEndTimeMs, raffleSeconds, roundId, status, stopSound, winSectorIndex, total])

  const isBetting = isWheel500BettingStatus(status)
  const isSpinning = isWheel500SpinningStatus(status)
  const isResult = isWheel500ResultStatus(status)
  const showGlow = isBetting || isSpinning || isResult
  // Янтарное/магента свечение при кручении и результате
  const showHotGlow = isSpinning || isResult
  // Цветное свечение по зоне выигрыша при результате
  const winZoneHex = isResult && winZone ? (WHEEL500_ZONE_CONFIG[winZone]?.hex || null) : null

  // Сегменты внешнего кольца (минималистичный стиль с зазором между секторами)
  const ringSegments = useMemo(() => {
    const gapDeg = Math.min(1.8, sectorDeg * 0.35)
    const innerRadius = 43
    const outerRadius = 46.5

    return sectors.map((sector, index) => {
      const start = WHEEL_START_ANGLE + index * sectorDeg + gapDeg / 2
      const end = WHEEL_START_ANGLE + (index + 1) * sectorDeg - gapDeg / 2

      return {
        index,
        color: getZoneHex(sector.zone),
        path: getSectorArcPath(innerRadius, outerRadius, start, end)
      }
    })
  }, [sectors, sectorDeg])

  const rotatingStyle = {
    transform: `rotate(${rotation}deg)`,
    '--spin-from': `${spinFromRotation}deg`,
    '--spin-to': `${rotation}deg`,
    animation: isAnimating ? `wheel-raffle-spin ${spinDurationMs}ms cubic-bezier(0.1, 0.25, 0.25, 1) -${spinElapsedMs}ms both` : 'none',
    transition: isAligning ? `transform ${ALIGN_DURATION_MS}ms cubic-bezier(0.25, 0.1, 0.25, 1)` : 'none'
  }

  return (
    <div className="w-full min-w-0 p-3">
      <div className="relative mx-auto aspect-square w-full max-w-[360px]">
        {/* Динамическое внешнее свечение */}
        <div
          className={`pointer-events-none absolute -inset-[7%] z-0 rounded-full transition-opacity duration-1000 ease-out ${showGlow ? 'opacity-100' : 'opacity-0'}`}
        >
          {/* Янтарное/магента — при кручении и результате */}
          <div
            className={`absolute inset-0 rounded-full blur-xl animate-[spin_4.8s_linear_infinite] bg-[conic-gradient(from_0deg,rgba(251,191,36,0.1),rgba(245,158,11,0.5),rgba(251,191,36,0.1),rgba(236,72,153,0.42),rgba(251,191,36,0.1))] transition-opacity duration-700 ease-out ${showHotGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          <div
            className={`absolute inset-3 rounded-full blur-2xl animate-[pulse_2.4s_ease-in-out_infinite] bg-[radial-gradient(circle,rgba(251,191,36,0.42)_0%,rgba(245,158,11,0.22)_38%,rgba(236,72,153,0)_72%)] transition-opacity duration-700 ease-out ${showHotGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          {/* Циан/тил — при ожидании ставок */}
          <div
            className={`absolute inset-0 rounded-full blur-xl animate-[spin_5.6s_linear_infinite] bg-[conic-gradient(from_0deg,rgba(34,211,238,0.08),rgba(20,184,166,0.38),rgba(34,211,238,0.08),rgba(14,165,233,0.42),rgba(34,211,238,0.08))] transition-opacity duration-700 ease-out ${!showHotGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          <div
            className={`absolute inset-3 rounded-full blur-2xl animate-[pulse_3s_ease-in-out_infinite] bg-[radial-gradient(circle,rgba(34,211,238,0.36)_0%,rgba(20,184,166,0.18)_38%,rgba(20,184,166,0)_72%)] transition-opacity duration-700 ease-out ${!showHotGlow ? 'opacity-100' : 'opacity-0'}`}
          />
          {/* Цветной акцент по выигрышной зоне при результате */}
          {winZoneHex && (
            <div
              className="absolute inset-6 rounded-full blur-3xl animate-[pulse_1.8s_ease-in-out_infinite] transition-opacity duration-1000"
              style={{
                background: `radial-gradient(circle, ${winZoneHex}66 0%, ${winZoneHex}22 40%, transparent 72%)`,
                opacity: isResult ? 1 : 0
              }}
            />
          )}
        </div>

        {/* База колеса */}
        <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_20%,rgba(88,115,132,0.4)_0%,rgba(28,42,54,0.26)_34%,rgba(8,15,23,0.94)_70%)] shadow-[inset_0_0_60px_rgba(0,0,0,0.5)]" />

        {/* Вращающееся кольцо сегментов */}
        <div
          className="absolute inset-0"
          style={rotatingStyle}
          onAnimationEnd={(event) => {
            if (event.animationName !== 'wheel-raffle-spin') return
            hapticImpact('heavy')
            setIsAnimating(false)
          }}
          onTransitionEnd={(event) => {
            if (event.propertyName === 'transform') setIsAligning(false)
          }}
        >
          <svg viewBox="0 0 100 100" className="h-full w-full">
            {ringSegments.map((segment) => (
              <path
                key={segment.index}
                d={segment.path}
                fill={segment.color}
                opacity="0.95"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="0.12"
              />
            ))}
          </svg>
        </div>

        <div className="pointer-events-none absolute inset-0 z-20">
          <div
            className="absolute inset-0"
            style={{
              clipPath: `circle(${MAGNIFIER_RADIUS_PERCENT}% at 50% ${MAGNIFIER_CENTER_Y_PERCENT}%)`
            }}
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(18,28,40,1)_0%,rgba(10,18,30,1)_46%,rgba(6,12,22,1)_100%)]" />

            <div
              className="absolute inset-0"
              style={{
                transform: `scale(${MAGNIFIER_SCALE})`,
                transformOrigin: `50% ${MAGNIFIER_ZOOM_ORIGIN_Y_PERCENT}%`
              }}
            >
              <div
                className="absolute inset-0"
                style={rotatingStyle}
              >
                <svg viewBox="0 0 100 100" className="h-full w-full">
                  {ringSegments.map((segment) => (
                    <path
                      key={`magnifier-${segment.index}`}
                      d={segment.path}
                      fill={segment.color}
                      opacity="1"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="0.11"
                    />
                  ))}
                </svg>
              </div>
            </div>
          </div>

          <div
            className="absolute left-1/2 z-10 -translate-x-1/2 overflow-hidden rounded-full border border-white/75 bg-transparent shadow-[0_0_0_1px_rgba(255,255,255,0.12),0_0_22px_rgba(34,211,238,0.26),0_12px_28px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.45),inset_0_-10px_24px_rgba(0,0,0,0.3)]"
            style={{
              top: `${MAGNIFIER_TOP_PERCENT}%`,
              width: `${MAGNIFIER_SIZE_PERCENT}%`,
              height: `${MAGNIFIER_SIZE_PERCENT}%`
            }}
          >
            <div className="absolute inset-[1.5px] rounded-full border border-white/40" />
            <div className="absolute left-[14%] top-[8%] h-[30%] w-[58%] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.35)_0%,rgba(255,255,255,0.18)_34%,rgba(255,255,255,0)_74%)]" />
          </div>
        </div>

        {/* Центральная область */}
        <div
          className="absolute inset-[16%] rounded-full border border-white/5 shadow-[inset_0_0_42px_rgba(0,0,0,0.55)] transition-colors duration-500"
          style={
            isResult && winZone
              ? { backgroundColor: WHEEL500_ZONE_CONFIG[winZone]?.hex || '#0a0f14' }
              : { background: 'radial-gradient(circle,rgba(6,12,18,0.96)_0%,rgba(5,10,16,0.98)_68%)' }
          }
        />

        {/* Значение в центре */}
        <div className="absolute inset-[16%] z-10 rounded-full flex items-center justify-center">
          {isWheel500BettingStatus(status) && (
            <span
              className="text-center font-bold font-mono tabular-nums tracking-tight text-white"
              style={{ fontSize: '2.4rem' }}
            >
              {formatTimer(countdownMs)}
            </span>
          )}

          {isWheel500SpinningStatus(status) && (
            <span
              className="text-center font-semibold tracking-tight text-white/30"
              style={{ fontSize: '1.6rem' }}
            >
              ···
            </span>
          )}

          {isResult && winZone && (
            <span
              className="text-center font-bold tracking-tight text-white"
              style={{ fontSize: '2.6rem' }}
            >
              {WHEEL500_ZONE_CONFIG[winZone]?.label || winZone}
            </span>
          )}
        </div>

        {/* Верхний указатель под сектором */}
        <div className="absolute left-1/2 top-[11.5%] z-20 -translate-x-1/2">
          <div className="h-0 w-0 border-l-[9px] border-r-[9px] border-b-[15px] border-l-transparent border-r-transparent border-b-white drop-shadow-[0_0_8px_rgba(255,255,255,0.55)]" />
        </div>
      </div>
    </div>
  )
}

export default Wheel500Wheel
